import EventEmitter from 'eventemitter3';
import { UploadEvent } from '../../constants/upload-event';
import { UploadQueueEvent } from '../../constants/upload-queue-event';
import { UploadStatus } from '../../constants/upload-status';
import { createDeferred } from '../../utils/promise/defer';
import { noop } from '../../utils/utils';
import { toFileToUpload } from './file-to-upload';
import { errorName } from '../../utils/tusProtocol/utils';
const uploadQueue = [];
let maxParallelUploads = 1;
let uploadIsInProgress = false;
let currentUploadedTotal = 0;
let deferred = null;
let resumeUploadAfterErrorTimeoutId;
const eventEmitter = new EventEmitter();
window.addEventListener('online', resumeUploadAfterNetworkError);
function subscribeResumeUploadAfterError() {
  if (!resumeUploadAfterErrorTimeoutId) {
    resumeUploadAfterErrorTimeoutId = window.setInterval(function () {
      resumeUploadAfterError();
    }, 5000);
  }
}
function unSubscribeResumeUploadAfterError() {
  window.clearTimeout(resumeUploadAfterErrorTimeoutId);
  resumeUploadAfterErrorTimeoutId = null;
}
subscribeResumeUploadAfterError();
const hasWaitingFiles = () => uploadQueue.some(fileToUpload => fileToUpload.status === UploadStatus.Waiting);
const getWaitingFiles = () => uploadQueue.filter(fileToUpload => fileToUpload.status === UploadStatus.Waiting);
const getNetworkErrorFiles = () => uploadQueue.filter(fileToUpload => fileToUpload.status === UploadStatus.Failed && fileToUpload.isTus && fileToUpload.error.name === errorName.NETWORK_ERROR);
const getShouldTryToResumeFiles = () => uploadQueue.filter(fileToUpload => fileToUpload.isInAutoResume());
const hasRetryableFiles = () => uploadQueue.some(fileToUpload => fileToUpload.canRetry());
const getInProgressFiles = () => uploadQueue.filter(fileToUpload => [UploadStatus.Uploading, UploadStatus.Processing].indexOf(fileToUpload.status) !== -1);
const hasInProgressFiles = () => uploadQueue.some(fileToUpload => [UploadStatus.Uploading, UploadStatus.Processing].indexOf(fileToUpload.status) !== -1);
const processItemProgress = fileToUpload => progress => {
  if ([UploadStatus.Failed, UploadStatus.Succeeded].indexOf(fileToUpload.status) === -1) {
    fileToUpload.status = progress.status;
  } else {
    progress.status = fileToUpload.status;
  }
  const fileSize = fileToUpload.getFileSize();
  fileToUpload.bytesLoaded = progress.loaded >= fileSize ? fileSize : progress.loaded;
  progress.file = fileToUpload;
  if (fileToUpload.canReportStatusChange) {
    eventEmitter.emit(UploadQueueEvent.FileStatusChanged, {
      bytesTotalUploaded: currentUploadedTotal + calculateItemBytesLoaded(fileToUpload, progress),
      bytesTotal: getBytesTotal(),
      currentFileProgress: progress,
      currentFileQueueIdx: uploadQueue.indexOf(fileToUpload),
      queue: uploadQueue
    });
  }
};
function resumeUploads(filesToResume) {
  filesToResume.forEach(fileToUpload => {
    fileToUpload.signUpForResume(true);
  });
}
function resumeUploadAfterNetworkError() {
  const filesToResume = getNetworkErrorFiles();
  resumeUploads(filesToResume);
}
function resumeUploadAfterError() {
  const filesToResume = getShouldTryToResumeFiles();
  if (filesToResume.length === 0) {
    unSubscribeResumeUploadAfterError();
  }
  filesToResume.forEach(fileToUpload => {
    fileToUpload.cancelAndProgressConfig.tusOptions.tryToResume().then(isResumeSuccess => {
      if (isResumeSuccess) {
        fileToUpload.signUpForResume(true);
      } else {
        subscribeResumeUploadAfterError();
      }
    });
  });
}
function dynamicPromiseReduce() {
  if (!hasWaitingFiles() && !hasInProgressFiles()) {
    deferred.resolve();
  }
  const isBypassingMaxConcurrency = file => {
    var _file$options;
    return !!(file != null && (_file$options = file.options) != null && _file$options.bypassMaxParallelUploads);
  };
  const waitingFiles = getWaitingFiles();
  const filesBypassingMaxConcurrency = waitingFiles.filter(isBypassingMaxConcurrency);
  const regularFiles = waitingFiles.filter(file => !isBypassingMaxConcurrency(file));
  const availableSlots = maxParallelUploads - getInProgressFiles().filter(file => !isBypassingMaxConcurrency(file)).length;
  const filesToStartUpload = [...filesBypassingMaxConcurrency, ...regularFiles.slice(0, availableSlots)];
  if (filesToStartUpload.length === 0) {
    return deferred.promise;
  }
  filesToStartUpload.forEach(fileToUpload => {
    const fileSize = fileToUpload.getFileSize();
    processItemProgress(fileToUpload)({
      status: UploadStatus.Uploading,
      total: fileSize,
      loaded: 0
    });
    const uploadPromise = fileToUpload.upload();
    uploadPromise.then(res => {
      dynamicPromiseReduce();
      subscribeResumeUploadAfterError();
      return res;
    }).catch(() => {
      dynamicPromiseReduce();
    });
  });
  return deferred.promise;
}
function getBytesTotal() {
  return uploadQueue.reduce((sum, fileToUpload) => sum + fileToUpload.getFileSize(), 0);
}
function calculateItemBytesLoaded(fileToUpload, progress) {
  switch (progress.status) {
    case UploadStatus.Uploading:
      return Math.min(progress.loaded, fileToUpload.getFileSize());
    case UploadStatus.Succeeded:
    case UploadStatus.Processing:
      return fileToUpload.getFileSize();
    case UploadStatus.Failed:
    default:
      return 0;
  }
}
function startUploadQueue() {
  if (uploadIsInProgress) {
    dynamicPromiseReduce();
    return;
  }
  deferred = createDeferred();
  uploadIsInProgress = true;
  eventEmitter.emit(UploadQueueEvent.UploadQueueStarted, uploadQueue);
  dynamicPromiseReduce().then(() => {
    uploadIsInProgress = false;
    eventEmitter.emit(UploadQueueEvent.UploadQueueFinished, uploadQueue);
  }).catch(noop);
}
function addProgressNotifications(fileToUpload) {
  const processCurrentItemProgress = processItemProgress(fileToUpload);
  const fileSize = fileToUpload.getFileSize();
  fileToUpload.cancelAndProgressConfig.progressEventEmitter.on(UploadEvent.Upload, processCurrentItemProgress);
  const unsubscribe = () => fileToUpload.cancelAndProgressConfig.progressEventEmitter.off(UploadEvent.Upload, processCurrentItemProgress);
  fileToUpload.deferred.promise.then(result => {
    fileToUpload.serverResponse = result;
    processCurrentItemProgress({
      status: UploadStatus.Succeeded,
      total: fileSize,
      loaded: fileSize
    });
    currentUploadedTotal += fileSize;
    unsubscribe();
  }).catch(reason => {
    fileToUpload.serverResponse = reason;
    processCurrentItemProgress({
      status: UploadStatus.Failed,
      total: fileSize,
      loaded: 0
    });
    unsubscribe();
  });
}
function addRestartOnRetry(fileToUpload) {
  const nativeRetry = fileToUpload.signUpForRetry;
  fileToUpload.signUpForRetry = function () {
    nativeRetry.apply(fileToUpload, arguments);
    addProgressNotifications(fileToUpload);
    startUploadQueue();
  };
}
function addRestartOnResume(fileToUpload) {
  const nativeRetry = fileToUpload.signUpForResume;
  fileToUpload.signUpForResume = function () {
    nativeRetry.apply(fileToUpload, arguments);
    addProgressNotifications(fileToUpload);
    startUploadQueue();
  };
}
async function clearQueue() {
  if (uploadIsInProgress) {
    throw new Error('Upload queue can not be cleared while upload is running');
  }
  uploadQueue.length = 0;
  currentUploadedTotal = 0;
}
function removeItemFromQueue(file) {
  if (!uploadQueue.length) {
    return;
  }
  const itemIndex = uploadQueue.indexOf(uploadQueue.find(uploadingFile => uploadingFile.id === file.id));
  if (itemIndex !== -1) {
    uploadQueue.splice(itemIndex, 1);
  }
  eventEmitter.emit(UploadQueueEvent.QueueChanged, uploadQueue);
}
function upload(fileSources) {
  fileSources = fileSources || [];
  if (!uploadIsInProgress && !hasRetryableFiles()) {
    clearQueue();
  }
  const mappedFiles = fileSources.map(toFileToUpload).map(fileToUpload => {
    addProgressNotifications(fileToUpload);
    addRestartOnRetry(fileToUpload);
    addRestartOnResume(fileToUpload);
    uploadQueue.push(fileToUpload);
    return fileToUpload;
  });
  eventEmitter.emit(UploadQueueEvent.QueueChanged, uploadQueue);
  startUploadQueue();
  return mappedFiles;
}
function cancelAllUploads() {
  uploadQueue.forEach(uploadingFile => {
    uploadingFile.abort();
  });
}
export const Uploader = {
  on: eventEmitter.on.bind(eventEmitter),
  off: eventEmitter.off.bind(eventEmitter),
  upload,
  cancelAllUploads,
  clearQueue,
  removeItemFromQueue,
  set maxParallelUploads(number) {
    maxParallelUploads = Number(number) || 1;
  },
  get maxParallelUploads() {
    return maxParallelUploads;
  },
  get uploadQueue() {
    return uploadQueue;
  },
  get uploadIsInProgress() {
    return uploadIsInProgress;
  },
  get currentUploadedTotal() {
    return currentUploadedTotal;
  }
};